/**
 * Message resolution with explicit fallback.
 *
 * §25 forbids hardcoded user-facing strings and requires translations to live
 * in separate resources. The design decision worth stating: a missing
 * translation falls back to English and **says so** through
 * {@link Translation.isFallback}, rather than rendering the raw key. A user who
 * sees an English sentence can still act on it; a user who sees
 * `error.notEnoughStorage` cannot.
 */
import { FALLBACK_LOCALE, type LocaleCode, type TextDirection, localeInfo } from './locales.js';
import type { MessageKey, PartialMessageCatalog } from './messages/en.js';
import { MESSAGE_KEYS, en } from './messages/en.js';
import { ko } from './messages/ko.js';
import * as partials from './messages/partials.js';

/** Values substituted into `{placeholder}` slots. */
export type MessageParams = Readonly<Record<string, string | number>>;

export interface Translation {
  readonly text: string;
  /** True when the English source was used because the locale lacked the key. */
  readonly isFallback: boolean;
}

const CATALOGS: Readonly<Record<LocaleCode, PartialMessageCatalog>> = {
  ko,
  en,
  ja: partials.ja,
  'zh-Hans': partials.zhHans,
  'zh-Hant': partials.zhHant,
  es: partials.es,
  fr: partials.fr,
  de: partials.de,
  pt: partials.pt,
  id: partials.id,
  vi: partials.vi,
  th: partials.th,
  ar: partials.ar,
  hi: partials.hi,
  ru: partials.ru,
};

/**
 * Substitute `{name}` placeholders.
 *
 * An unmatched placeholder is left intact rather than replaced with an empty
 * string: a visible `{count}` in a screenshot is a bug report, whereas a
 * silently blank sentence is a mystery.
 */
export function interpolate(template: string, params?: MessageParams): string {
  if (!params) return template;
  return template.replace(/\{(\w+)\}/g, (whole, name: string) => {
    const value = params[name];
    return value === undefined ? whole : String(value);
  });
}

export interface Translator {
  readonly locale: LocaleCode;
  readonly direction: TextDirection;
  /** Resolve a key to display text. */
  t(key: MessageKey, params?: MessageParams): string;
  /** Resolve with fallback information, for diagnostics and coverage reporting. */
  resolve(key: MessageKey, params?: MessageParams): Translation;
}

export function createTranslator(locale: LocaleCode): Translator {
  const info = localeInfo(locale);
  const catalog = CATALOGS[locale];
  const fallback = CATALOGS[FALLBACK_LOCALE];

  const resolve = (key: MessageKey, params?: MessageParams): Translation => {
    const local = catalog[key];
    if (local !== undefined) return { text: interpolate(local, params), isFallback: false };
    const base = fallback[key];
    if (base !== undefined) return { text: interpolate(base, params), isFallback: true };
    // Only reachable if a key is removed from English while still referenced.
    return { text: key, isFallback: true };
  };

  return {
    locale,
    direction: info.direction,
    t: (key, params) => resolve(key, params).text,
    resolve,
  };
}

export interface LocaleCoverage {
  readonly locale: LocaleCode;
  readonly translated: number;
  readonly total: number;
  readonly ratio: number;
  readonly missing: readonly MessageKey[];
}

/**
 * Translation completeness per locale.
 *
 * Reported rather than enforced: blocking a release on 100% coverage would
 * push someone to paste machine output into safety-critical strings, which §19
 * makes worse than an honest English fallback.
 */
export function localeCoverage(locale: LocaleCode): LocaleCoverage {
  const catalog = CATALOGS[locale];
  const missing = MESSAGE_KEYS.filter((k) => catalog[k] === undefined);
  const translated = MESSAGE_KEYS.length - missing.length;
  return {
    locale,
    translated,
    total: MESSAGE_KEYS.length,
    ratio: translated / MESSAGE_KEYS.length,
    missing,
  };
}

export function allLocaleCoverage(): readonly LocaleCoverage[] {
  return (Object.keys(CATALOGS) as LocaleCode[])
    .map(localeCoverage)
    .sort((a, b) => b.ratio - a.ratio || a.locale.localeCompare(b.locale));
}

/** Keys present in a translation but no longer defined in English. */
export function staleKeys(locale: LocaleCode): readonly string[] {
  const known = new Set<string>(MESSAGE_KEYS);
  return Object.keys(CATALOGS[locale]).filter((k) => !known.has(k));
}
